import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Users, Sparkles, TrendingUp, Share2, X, BadgeCheck, Crown, Circle } from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, CircleMarker, Tooltip, LayersControl } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../lib/api";
import { CITY_COORDS } from "../lib/madagascar_geo";

const MAD_CENTER = [-18.9, 46.8]; // lat, lon
const MAD_BOUNDS = [
  [-25.7, 43.0], // SW
  [-11.8, 50.6], // NE
];

export const CitiesMapPage = () => {
  const [data, setData] = useState({ cities: [], total_artists: 0, with_city: 0, without_city: 0 });
  const [hover, setHover] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);

  useEffect(() => {
    api.get("/stats/cities").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const plotted = useMemo(() => {
    return data.cities
      .map((c) => ({ ...c, coord: CITY_COORDS[c.city] }))
      .filter((c) => c.coord);
  }, [data.cities]);

  const maxCount = Math.max(1, ...plotted.map((c) => c.count));
  const radiusFor = (count) => 7 + (Math.log(count + 1) / Math.log(maxCount + 1)) * 18;

  const shareCity = async (c) => {
    const url = `https://voamada.com/carte`;
    const text = `🇲🇬 Ma ville ${c.city} compte déjà ${c.count} artiste${c.count > 1 ? "s" : ""} sur Voamada !\n\nRejoins-nous et fais grandir notre communauté locale 🌱\n${url}`;
    if (navigator.share) {
      try { await navigator.share({ title: `Voamada — ${c.city}`, text, url }); return; } catch { /* user aborted */ }
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Texte copié pour ${c.city} — colle-le sur tes réseaux !`);
    } catch {
      toast.error("Impossible de copier le lien.");
    }
  };

  return (
    <div className="px-4 md:px-12 py-10 max-w-7xl mx-auto pb-24" data-testid="cities-map-page">
      <p className="inline-flex items-center gap-2 tag-pill mb-4 bg-brand-yellow/10 border-brand-yellow/40 text-brand-yellow">
        <Sparkles size={12} /> Carte satellite des talents
      </p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-3 text-balance">
        D'où viennent{" "}
        <span className="bg-gradient-to-r from-[#E31B23] via-brand-orange to-[#EAB308] bg-clip-text text-transparent">
          les talents gasy
        </span>
        ?
      </h1>
      <p className="text-white/60 max-w-2xl mb-10">
        Vue satellite en temps réel. Clique sur une ville pour découvrir ses artistes —
        les <span className="text-emerald-400 font-semibold">pastilles vertes</span> indiquent ceux en ligne maintenant.
      </p>

      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <StatCard label="Total artistes" value={data.total_artists} accent />
        <StatCard label="Avec ville renseignée" value={data.with_city} />
        <StatCard label="Villes représentées" value={plotted.length} />
        <StatCard label="Sans ville" value={data.without_city} muted />
      </div>

      <div className="grid lg:grid-cols-[1fr_340px] gap-6">
        {/* MAP */}
        <div className="relative rounded-2xl border border-white/10 overflow-hidden" data-testid="madagascar-leaflet-map">
          <MapContainer
            center={MAD_CENTER}
            zoom={6}
            minZoom={5}
            maxZoom={12}
            maxBounds={MAD_BOUNDS}
            maxBoundsViscosity={0.9}
            scrollWheelZoom={true}
            className="h-[700px] w-full bg-[#0a0a0a]"
            attributionControl={false}
          >
            <LayersControl position="topright">
              <LayersControl.BaseLayer checked name="Satellite (Esri)">
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  attribution="Tiles &copy; Esri, Maxar, Earthstar Geographics"
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Topographique">
                <TileLayer
                  url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                  attribution="&copy; OpenTopoMap (CC-BY-SA)"
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Terrain Esri">
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Terrain_Base/MapServer/tile/{z}/{y}/{x}"
                  attribution="Tiles &copy; Esri"
                />
              </LayersControl.BaseLayer>

              {/* Labels overlay (noms de villes par-dessus le satellite) */}
              <LayersControl.Overlay checked name="Noms des lieux">
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                  attribution="Labels &copy; Esri"
                  opacity={0.9}
                />
              </LayersControl.Overlay>
            </LayersControl>

            {plotted.map((c) => {
              const [lon, lat] = c.coord;
              const r = radiusFor(c.count);
              const isHover = hover && hover.city === c.city;
              return (
                <CircleMarker
                  key={c.city}
                  center={[lat, lon]}
                  radius={r}
                  pathOptions={{
                    color: isHover ? "#EAB308" : "#F95738",
                    weight: isHover ? 3 : 2,
                    fillColor: "#E31B23",
                    fillOpacity: 0.75,
                  }}
                  eventHandlers={{
                    mouseover: () => setHover(c),
                    mouseout: () => setHover(null),
                    click: () => setSelectedCity(c),
                  }}
                >
                  <Tooltip direction="top" offset={[0, -r]} opacity={1} permanent={false}>
                    <div className="text-[11px] font-bold">
                      {c.city} — {c.count} artiste{c.count > 1 ? "s" : ""}
                    </div>
                  </Tooltip>
                </CircleMarker>
              );
            })}
          </MapContainer>

          {/* Hint overlay */}
          <div className="absolute bottom-3 left-3 rounded-lg bg-black/70 backdrop-blur-md border border-white/10 px-3 py-1.5 text-[10px] text-white/70 pointer-events-none z-[400]">
            🛰️ Clique sur une ville pour voir ses artistes
          </div>
        </div>

        {/* TOP CITIES list */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={16} className="text-brand-orange" />
            <h2 className="font-display text-lg font-black">Top villes</h2>
          </div>

          {plotted.length === 0 ? (
            <div className="text-sm text-white/50 py-8 text-center">
              <MapPin className="mx-auto text-white/30 mb-2" />
              Aucune ville renseignée pour l'instant.
              <br />
              <Link to="/register" className="text-brand-red hover:underline">Invite les artistes à s'inscrire</Link>
              {" "}pour voir la carte se remplir !
            </div>
          ) : (
            <ul className="space-y-2">
              {plotted.slice(0, 25).map((c, i) => (
                <li
                  key={c.city}
                  onClick={() => setSelectedCity(c)}
                  onMouseEnter={() => setHover(c)}
                  onMouseLeave={() => setHover(null)}
                  className={`group flex items-center gap-3 p-2.5 rounded-lg transition-colors cursor-pointer ${
                    hover?.city === c.city ? "bg-brand-red/10" : "hover:bg-white/[0.03]"
                  }`}
                  data-testid={`top-city-${c.city}`}
                >
                  <span className="w-6 text-xs font-black text-brand-orange text-right">{i + 1}</span>
                  <MapPin size={13} className="text-white/40 shrink-0" />
                  <span className="flex-1 truncate text-sm">{c.city}</span>
                  <span className="text-xs font-bold text-brand-red bg-brand-red/10 rounded-full px-2.5 py-0.5">
                    {c.count}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); shareCity(c); }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-white/40 hover:text-brand-red"
                    aria-label={`Partager ${c.city}`}
                    data-testid={`share-city-${c.city}`}
                  >
                    <Share2 size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <p className="text-[11px] text-white/35 mt-6 leading-relaxed">
            💡 Tu es artiste ? Indique ta ville dans{" "}
            <Link to="/dashboard" className="text-brand-orange hover:underline">ton profil</Link>{" "}
            pour apparaître sur la carte.
          </p>
        </div>
      </div>

      {selectedCity && (
        <CityArtistsModal
          city={selectedCity}
          onClose={() => setSelectedCity(null)}
          onShare={() => shareCity(selectedCity)}
        />
      )}
    </div>
  );
};

const CityArtistsModal = ({ city, onClose, onShare }) => {
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState({ artists: [], online: 0, count: 0 });

  useEffect(() => {
    setLoading(true);
    api
      .get(`/stats/cities/${encodeURIComponent(city.city)}/artists`)
      .then(({ data }) => setPayload(data))
      .catch(() => setPayload({ artists: [], online: 0, count: 0 }))
      .finally(() => setLoading(false));
  }, [city.city]);

  // Lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/75 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6 animate-in fade-in"
      onClick={onClose}
      data-testid="city-artists-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-2xl sm:rounded-2xl rounded-t-3xl border border-white/10 bg-gradient-to-b from-[#130a09] to-[#0a0605] shadow-2xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-6 border-b border-white/10">
          <div className="min-w-0">
            <p className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-brand-yellow font-bold mb-1">
              <MapPin size={11} /> Madagascar
            </p>
            <h3 className="font-display text-2xl sm:text-3xl font-black truncate" data-testid="city-modal-title">
              {city.city}
            </h3>
            <p className="text-sm text-white/60 mt-1">
              <span className="text-brand-red font-bold">{payload.count}</span> artiste{payload.count > 1 ? "s" : ""}
              {" · "}
              <span className="inline-flex items-center gap-1 text-emerald-400 font-semibold">
                <Circle size={8} fill="currentColor" className="animate-pulse" />
                {payload.online} en ligne
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={onShare}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-brand-orange/10 text-brand-orange hover:bg-brand-orange/20 border border-brand-orange/30 transition-colors"
              data-testid="city-modal-share"
            >
              <Share2 size={12} /> Partager
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/10 text-white/70"
              aria-label="Fermer"
              data-testid="city-modal-close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* List */}
        <div className="overflow-y-auto p-4 sm:p-6 space-y-2">
          {loading ? (
            <div className="py-14 text-center text-white/50 text-sm">Chargement…</div>
          ) : payload.artists.length === 0 ? (
            <div className="py-14 text-center text-white/50 text-sm">
              Aucun artiste pour l'instant dans cette ville.
              <br />
              <Link to="/register" className="text-brand-red hover:underline">Sois le premier à t'inscrire !</Link>
            </div>
          ) : (
            payload.artists.map((a) => (
              <Link
                key={a.user_id}
                to={`/u/${a.user_id}`}
                onClick={onClose}
                className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.06] border border-white/5 hover:border-brand-orange/30 transition-colors"
                data-testid={`city-artist-${a.user_id}`}
              >
                <div className="relative shrink-0">
                  {a.picture ? (
                    <img src={a.picture} alt={a.name} className="w-11 h-11 rounded-full object-cover" />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-brand-red to-brand-orange flex items-center justify-center font-black text-white">
                      {(a.name || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                  {a.is_online && (
                    <span
                      className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#130a09] animate-pulse"
                      title="En ligne"
                      data-testid={`online-dot-${a.user_id}`}
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="font-semibold truncate">{a.name}</span>
                    {a.is_verified && <BadgeCheck size={13} className="text-brand-yellow shrink-0" />}
                    {a.is_premium && <Crown size={12} className="text-brand-orange shrink-0" />}
                  </div>
                  <p className="text-xs text-white/50 truncate">
                    {a.followers_count} follower{a.followers_count > 1 ? "s" : ""}
                    {" · "}
                    {a.tracks_count} track{a.tracks_count > 1 ? "s" : ""}
                  </p>
                </div>
                {a.is_online ? (
                  <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-400">● live</span>
                ) : (
                  <span className="text-[10px] uppercase tracking-widest text-white/30">hors-ligne</span>
                )}
              </Link>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, accent, muted }) => (
  <div className={`rounded-xl border p-4 ${accent ? "border-brand-red/30 bg-brand-red/5" : "border-white/10 bg-white/[0.02]"}`}>
    <p className={`font-display text-3xl font-black ${accent ? "text-brand-red" : muted ? "text-white/40" : "text-white"}`}>
      {value ?? "—"}
    </p>
    <p className="text-[10px] uppercase tracking-widest text-white/50 mt-1">{label}</p>
  </div>
);

export default CitiesMapPage;
